import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ButtonWithSpinner } from '@eika/button-with-spinner';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import { useLoanDetailMutation } from '../../../fetchHooks/useLoans';
import type { Account, AccountId, AccountNumber } from '../../../generated-types';
import { useTransferAccountsContext } from '../../../hooks/useAccountState';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAccountTransferFrom } from '../../../services/accountFilters';
import { AnalyticsEvents, trackEvent } from '../../../services/analytics';
import isFixedInterestLoans from '../../../utils/boolean/isFixedInterestLoans';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { Content } from '../../shared/Content';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import { ErrorCardWrapper } from '../../shared/ErrorCardWrapper';
import AccountsEmptyInfo from '../AccountsEmptyInfo';
import TransactionResultInfo from '../TransactionResultInfo';
import InvalidFromAccountInfo, { TransactionType } from '../Transfer/InvalidFromAccountInfo';
import DownpaymentFields from './DownpaymentFields';
import { DownpaymentActionType } from './form/actions';
import useFormContext from './form/useFormContext';
import useCanMakeDownpayment from './hooks/useCanMakeDownpayment';

export interface DownpaymentProps {
    onClose: (openPayment: boolean) => void;
    account?: Account;
    loanAccount?: Account;
    isLoading?: boolean;
}

const DownpaymentForm: React.FC<DownpaymentProps> = ({ onClose, account, loanAccount, isLoading = false }) => {
    const translated = useTranslation();
    const fromAccountId = useRef<AccountId>();
    const { state, handleChange, checkFormValidity, mutation } = useFormContext();
    const { canMakeDownpayment, isLoading: fromAccountsLoading, isError: fromAccountsError } = useCanMakeDownpayment();

    const { targetFromAccount, targetToAccount } = useTransferAccountsContext();

    const onSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            const action = async (): Promise<void> => {
                event.preventDefault();
                if ((await checkFormValidity()) && loanAccount) {
                    trackEvent(
                        AnalyticsEvents.DOWNPAYMENT,
                        ANALYTICS_CATEGORIES.DOWNPAYMENT,
                        'create_downpayment',
                        'start',
                    );
                    await mutation.mutateAsync({ ...state, loanAccount });
                } else {
                    trackEvent(AnalyticsEvents.DOWNPAYMENT, ANALYTICS_CATEGORIES.DOWNPAYMENT, 'validation_error');
                }
            };
            return action();
        },
        [loanAccount, mutation, checkFormValidity, state],
    );

    useEffect(() => {
        if (account && !state.fromAccount) {
            if (isValidAccountTransferFrom(account)) {
                handleChange({
                    type: DownpaymentActionType.FROM_ACCOUNT_SELECTED,
                    payload: account.accountNumber,
                });
            }
        }
    }, [account, handleChange, state]);

    useEffect(() => {
        if (
            state.fromAccount &&
            targetFromAccount &&
            state.fromAccount.value === targetFromAccount.accountNumber.value
        ) {
            fromAccountId.current = targetFromAccount.accountId;
        }
    }, [state.fromAccount, targetFromAccount]);

    const isInvalidAccount = !!account && !state.fromAccount && !isValidAccountTransferFrom(account);

    const retryTransfer = () => {
        mutation.reset();
    };

    const loanDetails = useLoanDetailMutation();

    const selectLoanAccount = useCallback(
        (item?: AccountNumber) => {
            handleChange({ type: DownpaymentActionType.TO_ACCOUNT_SELECTED, payload: item });
            if (item && targetToAccount?.accountId && item.value === targetToAccount?.accountNumber.value) {
                loanDetails.mutate(targetToAccount.accountId);
            }
        },
        [handleChange, loanDetails, targetToAccount],
    );

    const isFixedLoan = useMemo(() => {
        return isFixedInterestLoans(loanDetails.data);
    }, [loanDetails.data]);

    const selectLoanCallback = useRef(selectLoanAccount);

    useEffect(() => {
        if (loanAccount) {
            selectLoanCallback.current(loanAccount.accountNumber);
        }
    }, [loanAccount, selectLoanCallback]);

    if (mutation.isError || mutation.isSuccess) {
        return (
            <TransactionResultInfo
                type="downpayment"
                error={mutation.error}
                retry={retryTransfer}
                fromAccountId={fromAccountId.current}
                onClose={() => onClose(false)}
            />
        );
    }

    if (isInvalidAccount && !state.showFromAccounts) {
        return (
            <InvalidFromAccountInfo
                onSelect={() => handleChange({ type: DownpaymentActionType.SHOW_FROM_ACCOUNTS })}
                account={account}
                transactionType={TransactionType.DOWNPAYMENT}
            />
        );
    }

    if (fromAccountsError) {
        return (
            <Content>
                <ErrorCardWrapper isModal defaultError={translated.accountViewErrorMessage} />
            </Content>
        );
    }

    if (!canMakeDownpayment && !fromAccountsLoading) {
        return <AccountsEmptyInfo title={translated.downpayment.noTransferFromAccount} />;
    }

    return (
        <>
            <h2 className="title">{translated.loans.extraDownpayment}</h2>
            <TransactionForm onSubmit={onSubmit} eightCol noDateField>
                <DownpaymentFields
                    shouldFocusFirstElement
                    loanAccount={loanAccount}
                    isLoading={isLoading || fromAccountsLoading}
                    selectLoanAccount={{
                        mutate: selectLoanAccount,
                        isLoading: loanDetails.isPending,
                    }}
                    isFixedLoan={isFixedLoan}
                    locked={mutation.isPending}
                />
                <TransactionSubmitWrapper>
                    <ButtonWithSpinner
                        buttonType="positive"
                        type="submit"
                        disabled={loanDetails.data?.loanCategory && isFixedLoan}
                        showSpinner={mutation.isPending}
                    >
                        {translated.payNow}
                    </ButtonWithSpinner>
                </TransactionSubmitWrapper>
            </TransactionForm>
            <DismissKeyboardStickyFooter />
        </>
    );
};

export default DownpaymentForm;
